<script setup lang="ts">
import { ref, useTemplateRef, computed } from 'vue';
import { clearCorruptDataTableState } from 'shared/common/dataTableState';
import { useDataTableMultiSort } from 'shared/common/composables/useDataTableMultiSort';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import { useColumnFilters } from 'shared/common/composables/useColumnFilters';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import ColumnFilterCell from 'shared/common/components/ColumnFilterCell.vue';
import { Button, Column, DataTable, Card, InputText, Menu } from 'primevue';
import type { DataTableRowDoubleClickEvent, DataTableRowClickEvent } from 'primevue';
import type { MenuItem } from 'primevue/menuitem';
import EditVehicleDialog from '@/vehicles/components/EditVehicleDialog.vue';
import DeleteVehicleDialog from '@/vehicles/components/DeleteVehicleDialog.vue';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import MainLayout from '@/common/components/MainLayout.vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import { useVehicleAuditApi } from '@/audit/api/vehicleAuditApi';
import { useVehicleStore } from 'shared/vehicles/stores/vehicleStore';
import { useRefreshVehicleStore } from './stores/refreshVehicleStore';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const actionFailedAlert = useActionFailedAlert();
const vehicleAuditApi = useVehicleAuditApi();
const vehicleStore = useVehicleStore();
const refreshVehicleStore = useRefreshVehicleStore();

const vehicles = computed(() => {
  const result = vehicleStore.vehicles.map<VehicleItem>(v => {
    return {
      id: v.id,
      name: v.name,
      type: translations.value.vehicleType(v.type),
      color: v.color,
      capacity: v.capacity,
      nameSortOrder: 0
    }
  });

  result.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  for (let i = 0; i < result.length; i++) {
    const item = result[i];
    if (item) {
      item.nameSortOrder = i;
    }
  }

  return result;
});
const selectedVehicles = ref<VehicleItem[]>([]);
const { loading } = storeToRefs(refreshVehicleStore);
const menuVehicleId = ref<string>();
const { multiSortMeta, sortBadgeClass } = useDataTableMultiSort();

interface VehicleColumn extends ColumnField {
  sortable: boolean;
  sortField?: string;
  cellClass?: string;
  kind: 'text' | 'color';
}

const columnDefinitions = computed<VehicleColumn[]>(() => [
  { field: 'name', header: translations.value.generic.name, sortable: true, sortField: 'nameSortOrder', cellClass: 'vehicle-name', kind: 'text', filterType: 'text' },
  { field: 'type', header: translations.value.generic.type, sortable: true, kind: 'text', filterType: 'text' },
  { field: 'capacity', header: translations.value.generic.capacity, sortable: true, kind: 'text', filterType: 'numeric' },
  { field: 'color', header: translations.value.generic.color, sortable: false, kind: 'color' },
]);
const { orderedColumns, selectedColumns, setSelectedColumns, setOrder, reset: resetColumns } =
  useColumnSelector<VehicleColumn>({ storageKey: 'vehicles-table-columns', columns: columnDefinitions });

const { filters, filteredCount, hasActiveFilters, clearFilters, onFilter } = useColumnFilters({
  columns: columnDefinitions,
  totalCount: () => vehicles.value.length,
});

const vehicleMenu = useTemplateRef('vehicle-menu');
const editDialog = useTemplateRef('edit-dialog');
const deleteDialog = useTemplateRef('delete-dialog');
const auditDialog = useTemplateRef('audit-dialog');


const menuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const vehicleId = menuVehicleId.value;
      if (!vehicleId) return;
      showVehicle(vehicleId);
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const vehicleId = menuVehicleId.value;
      if (!vehicleId) return;
      deleteDialog.value?.show(vehicleId);
    }
  },
  {
    label: translations.value.generic.audit,
    icon: 'pi pi-search',
    async command() {
      const vehicleId = menuVehicleId.value;
      if (!vehicleId) return;

      const response = await vehicleAuditApi.execute({
        id: vehicleId
      });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      auditDialog.value?.show({
        audit: response.audit
      });
    }
  }
]);


interface VehicleItem {
  id: string;
  name: string;
  nameSortOrder: number;
  type: string;
  color: string | null;
  capacity: number | null;
}

// Clear corrupt persisted sort state before the DataTable restores it while rendering.
clearCorruptDataTableState('vehicles-table-state');

function onAddNewClick() {
  editDialog.value?.show({});
}

function onDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const vehicle = ev.data as VehicleItem;
  showVehicle(vehicle.id);
}

function showVehicle(vehicleId: string) {
  editDialog.value?.show({ vehicleId: vehicleId });
}

function onClick(ev: DataTableRowClickEvent) {
  const target = ev.originalEvent.target as HTMLElement | null;
  if (!target) return;
  const name = target.closest('.vehicle-name');
  if (!name) return;
  showVehicle(ev.data.id);
}

function toggleMenu(ev: Event, vehicleId: string) {
  menuVehicleId.value = vehicleId;
  vehicleMenu.value?.toggle(ev);
}

</script>

<template>

  <MainLayout>
    <Card class="vehicle-card">
      <template #title>
        <span>{{ translations.vehicles.vehicles }}</span>
        <div class="filter-info">
          <span v-if="hasActiveFilters && filteredCount < vehicles.length" class="filtered-count">
            ({{ translations.dataTable.showing }} {{ filteredCount }} {{ translations.dataTable.of }} {{ vehicles.length }})
          </span>
          <Button v-if="hasActiveFilters" type="button" severity="secondary" size="small" icon="pi pi-filter-slash"
            :label="translations.dataTable.clearFilters" @click="clearFilters" />
        </div>
        <Button class="add-button" :label="translations.generic.add" @click="onAddNewClick">
        </Button>
        <InputText class="search-field" autofocus v-model="filters.global.value"
          :placeholder="translations.generic.search" />
      </template>
      <template #content>
        <DataTable class="vehicle-table" sort-mode="multiple" v-model:multiSortMeta="multiSortMeta" :class="sortBadgeClass" v-model:selection="selectedVehicles"
          v-model:filters="filters" :value="vehicles" data-key="id" scrollable scroll-height="flex" meta-key-selection
          @row-dblclick="onDoubleClick" :loading="loading" @row-click="onClick" stateStorage="local" stateKey="vehicles-table-state" filterDisplay="menu" @filter="onFilter">
          <template #empty>{{ translations.vehicles.noVehiclesFound }}</template>
          <template #loading>{{ translations.vehicles.loadingVehicleData }}</template>
          <Column v-for="col of selectedColumns" :key="col.field" :field="col.field" :class="col.cellClass"
            :header="col.header" :sortable="col.sortable" :sort-field="col.sortField"
            :filter="!!col.filterType" :filterField="col.filterField ?? col.field"
            :showFilterMatchModes="col.showFilterMatchModes" :showFilterOperator="col.showFilterOperator"
            :dataType="col.filterType === 'numeric' ? 'numeric' : undefined">
            <template #body="{ data }">
              <template v-if="col.kind === 'color'">
                <div v-if="data.color" class="flex gap-2">
                  <div class="w-6 h-6 rounded" :style="{ backgroundColor: `${data.color}` }">
                  </div>
                </div>
              </template>
              <template v-else>
                {{ data[col.field] }}
              </template>
            </template>
            <template v-if="col.filterType" #filter="{ filterModel, filterCallback }">
              <ColumnFilterCell :filter-type="col.filterType" :filter-model="filterModel" :filter-callback="filterCallback"
                :options="col.filterOptions"
                :placeholder="translations.dataTable.filterBy + ' ' + col.header.toLocaleLowerCase()" />
            </template>
          </Column>
          <Column class="w-24 !text-end">
            <template #header>
              <ColumnSelectorButton :columns="orderedColumns" :modelValue="selectedColumns"
                @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="resetColumns" />
            </template>
            <template #body="vehicle">
              <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
                @click="(ev) => toggleMenu(ev, vehicle.data.id)"></Button>
            </template>
          </Column>
        </DataTable>
      </template>
    </Card>
  </MainLayout>

  <EditVehicleDialog ref="edit-dialog"></EditVehicleDialog>
  <DeleteVehicleDialog ref="delete-dialog"></DeleteVehicleDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <Menu ref="vehicle-menu" :model="menuItems" :popup="true" />

</template>

<style scoped>
:deep(.p-datatable-hoverable .p-datatable-selectable-row) {
  cursor: unset;
}

:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto 1rem auto;
  column-gap: 0.5rem;
  align-items: center;
}

.filter-info {
  grid-column: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.filtered-count {
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.add-button {
  grid-column: 3;
}

.search-field {
  grid-column: 5;
}

:deep(.vehicle-name) {
  cursor: pointer;
}

:deep(.vehicle-name:hover) {
  color: var(--p-primary-900);
  background-color: var;
  text-decoration: underline;
}

.vehicle-card {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
}
</style>
